import { Box, Grid, Heading, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { SectionLabel, PillButton } from '@denise-augusta/ui';
import { useSiteContent } from '@/providers/SiteContentProvider';
import { SiteFooter } from '@/layouts/PublicLayout';
import { RichText } from '@/components/RichText';

const CIRCLES_BG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 1600'><g fill='none' stroke='%231F2D5C' stroke-opacity='0.22' stroke-width='1.4'><circle cx='160' cy='190' r='90'/><circle cx='980' cy='240' r='170'/><circle cx='500' cy='620' r='110'/><circle cx='1300' cy='780' r='150'/><circle cx='270' cy='1010' r='210'/><circle cx='1150' cy='1240' r='120'/><circle cx='720' cy='1380' r='80'/><path d='M 0,420 Q 300,320 620,420 T 1240,420 T 1600,420'/><path d='M 0,1120 Q 400,1020 820,1120 T 1600,1120'/><path d='M 220,210 L 360,310'/><path d='M 870,1320 L 1020,1420'/><path d='M 1100,80 L 1240,180'/></g></svg>")`;

export function Services() {
  const content = useSiteContent();

  return (
    <Box
      css={{
        backgroundColor: '#F5F4F1',
        backgroundImage: CIRCLES_BG,
        backgroundRepeat: 'repeat',
        backgroundSize: '1500px 1500px',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* PAGE HEADER */}
      <Box
        pt={{ base: '14.25rem', md: '9.25rem' }}
        pb={{ base: '4rem', md: '3.5rem' }}
        pl={{ base: '1.8rem', md: '11rem' }}
        pr={{ base: '1.8rem', md: '3.5rem' }}
        bg="brand.wash"
        position="relative"
        overflow="hidden"
      >
        <Box
          display={{ base: 'none', md: 'block' }}
          position="absolute"
          right="6%"
          top="5rem"
          bottom="1.5rem"
          w="42%"
          maxW="520px"
          zIndex="0"
          pointerEvents="none"
          aria-hidden="true"
          css={{
            '& span': {
              position: 'absolute',
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              color: '#3D52A8',
              lineHeight: 1,
            },
          }}
        >
          <Box as="span" top="0" right="4%" fontSize="5rem" opacity="0.18">01</Box>
          <Box as="span" top="18%" right="62%" fontSize="3.8rem" opacity="0.18">02</Box>
          <Box as="span" top="32%" right="18%" fontSize="3.6rem" opacity="0.22">03</Box>
          <Box as="span" top="58%" right="65%" fontSize="3.4rem" opacity="0.2">04</Box>
          <Box as="span" top="74%" right="8%" fontSize="5rem" opacity="0.14">05</Box>
          <Box as="span" top="38%" right="32%" fontSize="7.5rem" opacity="0.6" color="#B5E061 !important">06</Box>
        </Box>

        <Box position="relative" zIndex="2">
          <SectionLabel display>What I Do</SectionLabel>
          <Heading
            as="h1"
            fontFamily="heading"
            fontSize="clamp(3.6rem, 8vw, 7rem)"
            fontWeight="300"
            lineHeight="0.95"
            color="brand.ink"
            mb="1.6rem"
          >
            Services
            <br />
            <Box as="em" fontStyle="italic" color="brand.mid">
              Offered
            </Box>
          </Heading>
          <Text fontFamily="heading" fontStyle="italic" fontSize="1.22rem" color="ink.soft" maxW="560px" lineHeight="1.7">
            Integrating seamlessly into your world, I handle the details so you can focus on what only you can do.
          </Text>
        </Box>
      </Box>

      {/* SERVICES GRID */}
      <Box as="main" maxW="1200px" mx="auto" px={{ base: '1.8rem', md: '3.5rem' }} pt={{ base: '3.5rem', md: '5rem' }} pb={{ base: '4rem', md: '6rem' }}>
        <RichText
          html={content.servicesNote}
          maxW="820px"
          mx="auto"
          mb="2.8rem"
          fontFamily="heading"
          fontStyle="italic"
          fontSize="1.16rem"
          color="ink.muted"
          lineHeight="1.75"
          textAlign="center"
          px="1.5rem"
        />
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap="1.5rem" mb="4rem">
          {content.services.filter((svc) => !svc.hidden).map((svc) => (
            <Box
              key={svc.number}
              bg="paper.white"
              border="1px solid"
              borderColor="paper.border"
              p="2.8rem 2.4rem"
              position="relative"
              overflow="hidden"
              borderRadius="16px"
              transition="transform 0.28s, box-shadow 0.28s, border-color 0.28s"
              role="group"
              css={{
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: '#3D52A8',
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.35s',
                },
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 48px rgba(61,82,168,0.12)',
                  borderColor: '#C5CDE4',
                },
                '&:hover::before': { transform: 'scaleX(1)' },
                '&:hover .svc-num': { color: '#B5E061' },
              }}
            >
              <Text
                className="svc-num"
                fontFamily="heading"
                fontSize="3.5rem"
                fontWeight="300"
                color="paper.greyPale"
                lineHeight="1"
                mb="0.8rem"
                transition="color 0.32s"
              >
                {svc.number}
              </Text>
              <Heading as="h3" fontFamily="heading" fontSize="1.4rem" fontWeight="400" color="brand.ink" mb="0.8rem" lineHeight="1.15">
                {svc.title}
              </Heading>
              <RichText html={svc.description} fontSize="0.86rem" color="ink.soft" lineHeight="1.85" mb="1.2rem" />
              <Box as="ul" listStyleType="none" borderTop="1px solid" borderTopColor="paper.linen" pt="1.1rem">
                {svc.includes.map((item) => (
                  <Box
                    as="li"
                    key={item}
                    fontSize="0.8rem"
                    color="ink.soft"
                    py="0.32rem"
                    display="flex"
                    alignItems="flex-start"
                    gap="0.65rem"
                  >
                    <Box as="span" color="brand.mid" flexShrink="0" fontSize="0.75rem" mt="0.05rem">
                      →
                    </Box>
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Grid>
      </Box>

      {/* PROCESS STRIP */}
      <Box bg="paper.off" px={{ base: '1.8rem', md: '3.5rem' }} pt={{ base: '4rem', md: '3rem' }} pb={{ base: '5rem', md: '4.5rem' }}>
        <Box maxW="1100px" mx="auto">
          <SectionLabel large mb="1.5rem">How It Works</SectionLabel>
          <Heading as="h2" fontFamily="heading" fontSize="clamp(2.2rem, 4vw, 3.5rem)" fontWeight="300" color="brand.ink" mb="3rem">
            Simple from{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              the start
            </Box>
          </Heading>
          <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={{ base: '2rem', lg: '2.5rem' }}>
            {content.process.map((step) => (
              <Box key={step.number} pt="1rem" position="relative" _before={{ content: '""', position: 'absolute', top: 0, left: 0, w: '32px', h: '3px', bg: 'brand.mid', borderRadius: '2px' }}>
                <Text fontFamily="heading" fontSize="3rem" fontWeight="300" color="brand.pale" lineHeight="1" mb="0.6rem">
                  {step.number}
                </Text>
                <Heading as="h4" fontFamily="heading" fontSize="1.2rem" fontWeight="400" color="brand.ink" mb="0.5rem">
                  {step.title}
                </Heading>
                <RichText html={step.body} fontSize="0.84rem" color="ink.soft" lineHeight="1.82" />
              </Box>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* CTA */}
      <Box bg="brand.wash" px={{ base: '1.8rem', md: '3.5rem' }} pt={{ base: '3.5rem', md: '2.8rem' }} pb={{ base: '4.5rem', md: '4rem' }} textAlign="center" borderTop="2.5px solid" borderTopColor="brand.lime">
        <Heading as="h2" fontFamily="heading" fontSize="clamp(2rem, 4vw, 3.2rem)" fontWeight="300" color="brand.ink" mb="1.5rem">
          Ready to{' '}
          <Box as="em" fontStyle="italic" color="brand.mid">
            Delegate?
          </Box>
        </Heading>
        <Text fontFamily="heading" fontStyle="italic" fontSize="1.05rem" color="ink.soft" mb="2rem">
          Let's find the right package for where you are right now.
        </Text>
        <PillButton asChild scheme="plain">
          <RouterLink to="/contact">Contact Me</RouterLink>
        </PillButton>
      </Box>

      <SiteFooter copyright={content.footer.copyright} />
    </Box>
  );
}
